import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useState, useCallback } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import classNames from 'classnames';
import { IS_FIREFOX } from '../../../helpers/BrowserHelpers';
import EntityTypes from '../../../types/EntityTypes';
import DndTypes from '../../../types/DndTypes';
import GutterBlockTypes, { HOVER_EVENT } from '../../../types/GutterBlockTypes';
import moveNodeTo from '../utils/moveNodeTo';
import DragButton from './DragButton';
import HorizontalDragLine from './HorizontalDragLine';
import { useEffectFromStore } from '../../utils/createStore';
import insertEntity from '../../utils/insertEntity';
export default function DragWrapper(_ref) {
  var editor = _ref.editor,
      node = _ref.node,
      store = _ref.store,
      showDragButton = _ref.showDragButton,
      canDragOrDrop = _ref.canDragOrDrop,
      showFullWidthButton = _ref.showFullWidthButton,
      attributes = _ref.attributes,
      children = _ref.children;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isMouseOver = _useState2[0],
      setMouseOver = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isHighlighted = _useState4[0],
      setHighlighted = _useState4[1];

  useEffectFromStore(store, 'highlightedBlock', function (highlightedBlock) {
    return setHighlighted(highlightedBlock && highlightedBlock.key === node.key);
  });

  var _useDrop = useDrop({
    accept: [DndTypes.DND_LINE, DndTypes.IMAGE_GALLERY, DndTypes.NOTE, NativeTypes.FILE],
    collect: function collect(monitor) {
      return {
        isDropHover: monitor.isOver({
          shallow: true
        })
      };
    },
    canDrop: function canDrop() {
      return canDragOrDrop;
    },
    drop: function drop(dragNode, monitor) {
      var item = monitor.getItem();

      if (item.type === undefined) {
        if (item.files) {
          return editor.command('addFilesBeforeNode', item.files, node);
        }

        return; // Unknown item dropped. Let's NOOP
      }

      if (item.type === DndTypes.NOTE) {
        editor.moveToStartOfNode(node);

        if (node.text !== '') {
          editor.splitBlock().moveToEndOfPreviousText();
        }

        return insertEntity(editor, null, item.data.title || 'Untitled', EntityTypes.NOTELINK, {
          noteId: item.data.noteId
        }).insertText(' ');
      }

      var dropResult = monitor.didDrop() && monitor.getDropResult();
      editor.withoutNormalizing(function () {
        if (dropResult) {
          // Drop is being handled by another component
          moveNodeTo(editor, dropResult.dragNode.key, dropResult.dropNode.key, dropResult.position);
          dropResult.callback();
        } else {
          moveNodeTo(editor, dragNode.key, node.key);
        }
      });
    }
  }),
      _useDrop2 = _slicedToArray(_useDrop, 2),
      isDropHover = _useDrop2[0].isDropHover,
      dropRef = _useDrop2[1];

  var _useDrag = useDrag({
    item: {
      key: node.key,
      type: DndTypes.DND_LINE
    },
    canDrag: function canDrag() {
      return canDragOrDrop;
    },
    collect: function collect(monitor) {
      return {
        isDragging: monitor.isDragging()
      };
    },
    begin: function begin() {
      return store.updateItem('isDraggingNode', true);
    },
    end: function end() {
      return store.updateItem('isDraggingNode', false);
    }
  }),
      _useDrag2 = _slicedToArray(_useDrag, 3),
      isDragging = _useDrag2[0].isDragging,
      dragRef = _useDrag2[1],
      dragPreviewRef = _useDrag2[2];

  var onDragStart = useCallback(function (event) {
    // Prevent uncontrolled drag'n'drop like dragging raw text
    // But let images be draggable to create image gallery
    if (event.target.tagName !== 'IMG') {
      event.preventDefault();
    }
  }, []);
  var onMouseMove = useCallback(function () {
    if (isMouseOver) return;
    editor.command('updateGutterBlock', {
      nodeId: node.key,
      gutterBlockType: GutterBlockTypes.BLOCK_PRESENCE,
      priority: 0,
      origin: HOVER_EVENT
    });
    setMouseOver(true);
  }, [editor, node, isMouseOver, setMouseOver]);
  var onMouseLeave = useCallback(function () {
    editor.command('updateGutterBlock', {
      nodeId: node.key,
      gutterBlockType: GutterBlockTypes.BLOCK_PRESENCE,
      origin: HOVER_EVENT
    });
    setMouseOver(false);
  }, [editor, node, setMouseOver]);
  return React.createElement("div", Object.assign({}, attributes, {
    ref: dropRef,
    className: classNames('note-editor-dnd-line-wrapper', {
      'active background-shade-50': isHighlighted
    }),
    onMouseMove: onMouseMove,
    onMouseLeave: onMouseLeave
  }), isDropHover && React.createElement(HorizontalDragLine, null), showDragButton && !IS_FIREFOX && React.createElement(DragButton, {
    dragRef: dragRef,
    node: node,
    editor: editor,
    isDragging: isDragging,
    isMouseOver: isMouseOver,
    canDragOrDrop: canDragOrDrop,
    showFullWidthButton: showFullWidthButton
  }), React.createElement("div", {
    ref: dragPreviewRef,
    onDragStart: onDragStart
  }, children));
}